import { useMemo } from "react";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  RadialBarChart,
  RadialBar,
  Tooltip,
} from "recharts";
import { motion } from "framer-motion";

/**
 * RadarScoresChart - animated radar (8 axes) for standard report scores.
 *
 * Props:
 *  - scores: { response_chance, interest, ... } 0..100
 *  - labels: { response_chance: "...", ... } translated labels
 *  - color: primary stroke color
 */
export function RadarScoresChart({ scores = {}, labels = {}, color = "#ff4458" }) {
  const data = useMemo(() => {
    const keys = [
      "response_chance",
      "interest",
      "meeting_chance",
      "chemistry",
      "ghosting_risk",
      "profile_strength",
      "photo_quality",
      "bio_quality",
    ];
    return keys.map((k) => {
      const raw = scores[k] ?? 0;
      // ghosting_risk: invert so the radar shape stays intuitive (higher = better)
      const value = k === "ghosting_risk" ? 100 - raw : raw;
      return {
        key: k,
        label: labels[k] || k,
        value,
        raw,
      };
    });
  }, [scores, labels]);

  return (
    <div className="relative h-[260px] w-full" data-testid="radar-scores-chart">
      <ResponsiveContainer>
        <RadarChart data={data} outerRadius="80%">
          <PolarGrid stroke="rgba(255,255,255,0.12)" />
          <PolarAngleAxis
            dataKey="label"
            tick={{ fill: "#dcdde6", fontSize: 10, fontWeight: 600 }}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 100]}
            tick={false}
            axisLine={false}
          />
          <Radar
            name="score"
            dataKey="value"
            stroke={color}
            fill={color}
            fillOpacity={0.35}
            strokeWidth={2}
            animationDuration={1200}
            animationEasing="ease-out"
            isAnimationActive
          />
          <Tooltip
            contentStyle={{
              background: "#0f1117",
              border: "1px solid #222636",
              borderRadius: 12,
              color: "#fff",
              fontSize: 12,
            }}
            formatter={(_v, _n, item) => [`${item?.payload?.raw ?? 0}`, item?.payload?.label]}
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* -------------------------------------------------------------- */

/**
 * BigFiveSpider - radar tuned for Big Five personality.
 *
 * Props:
 *  - personality: { openness, conscientiousness, extraversion, agreeableness, neuroticism }
 */
export function BigFiveSpider({ personality = {}, color = "#a855f7" }) {
  const labels = {
    openness: "Otwartość",
    conscientiousness: "Sumienność",
    extraversion: "Ekstrawersja",
    agreeableness: "Ugodowość",
    neuroticism: "Neurotyzm",
  };
  const data = useMemo(
    () =>
      Object.keys(labels).map((k) => ({
        key: k,
        label: labels[k],
        value: personality[k] ?? 0,
      })),
    [personality]
  );

  return (
    <div className="relative h-[260px] w-full" data-testid="bigfive-spider">
      <ResponsiveContainer>
        <RadarChart data={data} outerRadius="80%">
          <defs>
            <linearGradient id="bigfive-fill" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.5} />
              <stop offset="100%" stopColor="#ec4899" stopOpacity={0.25} />
            </linearGradient>
          </defs>
          <PolarGrid stroke="rgba(255,255,255,0.12)" />
          <PolarAngleAxis
            dataKey="label"
            tick={{ fill: "#dcdde6", fontSize: 11, fontWeight: 600 }}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 100]}
            tick={false}
            axisLine={false}
          />
          <Radar
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            fill="url(#bigfive-fill)"
            animationDuration={1200}
            isAnimationActive
          />
          <Tooltip
            contentStyle={{
              background: "#0f1117",
              border: "1px solid #222636",
              borderRadius: 12,
              color: "#fff",
              fontSize: 12,
            }}
            formatter={(v) => [`${v}`, "score"]}
          />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* -------------------------------------------------------------- */

/**
 * OverallRadial - shows a single overall score as a radial bar gauge.
 */
export function OverallRadial({ value = 0, label = "Overall", color = "#ff4458" }) {
  const data = [{ name: "score", value, fill: color }];
  return (
    <div className="relative h-[200px] w-full" data-testid="overall-radial">
      <ResponsiveContainer>
        <RadialBarChart
          innerRadius="65%"
          outerRadius="100%"
          data={data}
          startAngle={210}
          endAngle={-30}
        >
          <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
          <RadialBar
            background={{ fill: "rgba(255,255,255,0.08)" }}
            dataKey="value"
            cornerRadius={12}
            isAnimationActive
            animationDuration={1200}
          />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="font-display text-5xl font-extrabold dc-gradient-text"
        >
          {value}
        </motion.div>
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#a8adbd]">
          {label}
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- */

/**
 * ConversationHeatmap - takes the conversation text and builds a 12-cell
 * heat strip of "intensity" (proxy: token density per bucket).
 * If the text is missing, generates a representative pattern from the scores.
 *
 * Props:
 *  - text: string (conversation)
 *  - scores: object (used as fallback shape)
 */
export function ConversationHeatmap({ text = "", scores = {} }) {
  const cells = useMemo(() => {
    const CELLS = 14;
    const out = [];
    if (text && text.length > 30) {
      const chunkSize = Math.max(8, Math.floor(text.length / CELLS));
      for (let i = 0; i < CELLS; i++) {
        const slice = text.slice(i * chunkSize, (i + 1) * chunkSize);
        if (!slice) {
          out.push(0);
          continue;
        }
        const words = slice.split(/\s+/).filter(Boolean).length;
        const punct = (slice.match(/[!?.]/g) || []).length;
        // emoji & "!" pattern boost
        const boosts = (slice.match(/[!?\u{1F300}-\u{1FAFF}]/gu) || []).length;
        const score = Math.min(
          100,
          Math.round(words * 4 + punct * 6 + boosts * 8)
        );
        out.push(score);
      }
    } else {
      // Fallback: synthesize from scores so visual still looks meaningful
      const seed =
        (scores.response_chance ?? 50) * 0.7 +
        (scores.interest ?? 50) * 0.3;
      for (let i = 0; i < CELLS; i++) {
        const t = i / (CELLS - 1);
        // sine-like rising/falling curve
        const v =
          seed * (0.5 + 0.5 * Math.sin(t * Math.PI * 1.4 - 0.4)) +
          ((i * 13) % 23) -
          11;
        out.push(Math.max(8, Math.min(100, Math.round(v + 35))));
      }
    }
    return out;
  }, [text, scores]);

  const max = Math.max(...cells, 1);

  return (
    <div className="space-y-3" data-testid="conversation-heatmap">
      <div className="flex h-20 gap-1.5">
        {cells.map((v, i) => {
          const intensity = v / max;
          const bg =
            intensity > 0.75
              ? "#ff4458"
              : intensity > 0.55
              ? "#fd267a"
              : intensity > 0.35
              ? "#ff6036"
              : intensity > 0.18
              ? "#fdb022"
              : "#222636";
          const height = 30 + intensity * 70; // percent
          return (
            <motion.div
              key={i}
              className="relative flex flex-1 items-end overflow-hidden rounded-md"
              style={{ background: "rgba(255,255,255,0.04)" }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
            >
              <motion.div
                className="w-full rounded-md"
                initial={{ height: 0 }}
                animate={{ height: `${height}%` }}
                transition={{ duration: 0.6, delay: i * 0.04, ease: [0.32, 0.72, 0, 1] }}
                style={{
                  background: `linear-gradient(180deg, ${bg}cc, ${bg}55)`,
                  boxShadow: intensity > 0.4 ? `0 0 12px ${bg}66` : "none",
                }}
              />
            </motion.div>
          );
        })}
      </div>
      <div className="flex items-center justify-between text-[10px] font-medium uppercase tracking-wider text-[#a8adbd]">
        <span>Start</span>
        <span className="flex items-center gap-1">
          <span className="inline-block h-1.5 w-3 rounded-sm" style={{ background: "#222636" }} />
          <span className="inline-block h-1.5 w-3 rounded-sm" style={{ background: "#fdb022" }} />
          <span className="inline-block h-1.5 w-3 rounded-sm" style={{ background: "#fd267a" }} />
          <span className="inline-block h-1.5 w-3 rounded-sm" style={{ background: "#ff4458" }} />
        </span>
        <span>Finał</span>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- */

/**
 * PhotoCarousel - swipeable carousel for analyzed images (base64 data URIs).
 */
export function PhotoCarousel({ images = [], onIndex }) {
  if (!images || images.length === 0) {
    return (
      <div className="flex h-40 flex-col items-center justify-center rounded-2xl border border-dashed border-[#222636] bg-[#0f1117] text-center text-[#a8adbd]">
        <div className="text-3xl">📸</div>
        <p className="mt-2 text-xs">Brak załączonych zdjęć w tej analizie</p>
      </div>
    );
  }
  return <PhotoCarouselInner images={images} onIndex={onIndex} />;
}

import { useState } from "react";

function PhotoCarouselInner({ images, onIndex }) {
  const [idx, setIdx] = useState(0);
  const safeIdx = Math.min(idx, images.length - 1);
  const go = (d) => {
    const next = (safeIdx + d + images.length) % images.length;
    setIdx(next);
    onIndex?.(next);
  };
  const src = images[safeIdx];
  return (
    <div className="space-y-2" data-testid="photo-carousel">
      <div className="relative h-56 w-full overflow-hidden rounded-2xl border border-[#222636] bg-[#0f1117]">
        <motion.img
          key={safeIdx}
          src={src}
          alt={`Photo ${safeIdx + 1}`}
          className="absolute inset-0 h-full w-full object-cover"
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35 }}
        />
        <div className="pointer-events-none absolute inset-x-0 top-0 flex gap-1 p-3">
          {images.map((_, i) => (
            <span
              key={i}
              className={`h-0.5 flex-1 rounded-full transition-all ${
                i === safeIdx ? "bg-white" : "bg-white/30"
              }`}
            />
          ))}
        </div>
        <div className="absolute bottom-3 right-3 rounded-full bg-black/45 px-2 py-0.5 font-mono text-[11px] text-white backdrop-blur">
          {safeIdx + 1}/{images.length}
        </div>
        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              data-testid="carousel-prev"
              className="absolute left-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur transition hover:bg-black/65"
              aria-label="Previous"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              data-testid="carousel-next"
              className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur transition hover:bg-black/65"
              aria-label="Next"
            >
              ›
            </button>
          </>
        )}
      </div>
    </div>
  );
}
